import React, { useState, ChangeEvent, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ProfileFormData, PasswordFormData, User, formatErrorMessage } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faKey } from '@fortawesome/free-solid-svg-icons';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Profile update form
  const [profileData, setProfileData] = useState<ProfileFormData>({
    username: user?.username || '',
    email: user?.email || ''
  });
  
  // Password update form
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const handleProfileSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.put<User>(`/users/my-organization/${user?.id}`, profileData);
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password length
    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      await axios.put('/auth/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      setSuccess('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, color: '#1f2937' }}>Profile Settings</h1>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '1px solid #e5e7eb', 
        marginBottom: '24px' 
      }}>
        <button
          onClick={() => setActiveTab('profile')}
          style={{
            padding: '12px 24px',
            border: 'none',
            backgroundColor: 'transparent',
            borderBottom: activeTab === 'profile' ? '2px solid #3b82f6' : '2px solid transparent',
            color: activeTab === 'profile' ? '#3b82f6' : '#6b7280',
            cursor: 'pointer',
            fontWeight: activeTab === 'profile' ? '500' : '400'
          }}
        >
          Profile Information
        </button>
        <button
          onClick={() => setActiveTab('password')}
          style={{
            padding: '12px 24px',
            border: 'none',
            backgroundColor: 'transparent',
            borderBottom: activeTab === 'password' ? '2px solid #3b82f6' : '2px solid transparent',
            color: activeTab === 'password' ? '#3b82f6' : '#6b7280',
            cursor: 'pointer',
            fontWeight: activeTab === 'password' ? '500' : '400'
          }}
        >
          Change Password
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div style={{ 
          backgroundColor: '#fee2e2', 
          color: '#dc2626', 
          padding: '12px', 
          borderRadius: '6px', 
          marginBottom: '16px' 
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ 
          backgroundColor: '#dcfce7', 
          color: '#166534', 
          padding: '12px', 
          borderRadius: '6px', 
          marginBottom: '16px' 
        }}>
          {success}
        </div>
      )}

      {/* Profile Information Tab */}
      {activeTab === 'profile' && (
        <div className="card">
          <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Profile Information</h3>
          <form onSubmit={handleProfileSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Username
              </label>
              <input
                type="text"
                name="username"
                value={profileData.username}
                onChange={handleProfileChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Email
              </label>
              <input
                type="email"
                name="email"
                value={profileData.email}
                onChange={handleProfileChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Role
              </label>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f9fafb',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {user?.role === 'super_admin' ? 'Super Admin' : 
                 user?.role === 'organization_admin' ? 'Organization Admin' : 
                 'Organization User'}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Organization
              </label>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f9fafb',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {user?.organization?.name || 'No Organization'}
              </div>
            </div>
            <button 
              type="submit" 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#16a34a',
                border: '2px solid white',
                borderRadius: '8px',
                color: 'white',
                padding: '0',
                fontSize: '14px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                cursor: 'pointer',
                overflow: 'hidden',
                minWidth: '160px',
                minHeight: '40px',
                opacity: loading ? 0.7 : 1
              }}
              disabled={loading}
            >
              <div style={{
                backgroundColor: '#16a34a',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid rgba(255,255,255,0.3)'
              }}>
                <FontAwesomeIcon icon={faSave} style={{ fontSize: '14px' }} />
              </div>
              <div style={{
                padding: '10px 12px',
                flex: 1,
                textAlign: 'center'
              }}>
                {loading ? 'UPDATING...' : 'UPDATE PROFILE'}
              </div>
            </button>
          </form>
        </div>
      )}

      {/* Change Password Tab */}
      {activeTab === 'password' && (
        <div className="card">
          <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Change Password</h3>
          <form onSubmit={handlePasswordSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Current Password
              </label>
              <input
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                New Password
              </label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                minLength={6}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Password must be at least 6 characters long
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Confirm New Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                minLength={6}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <button 
              type="submit" 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#dc2626',
                border: '2px solid white',
                borderRadius: '8px',
                color: 'white',
                padding: '0',
                fontSize: '14px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                cursor: 'pointer',
                overflow: 'hidden',
                minWidth: '180px',
                minHeight: '40px',
                opacity: loading ? 0.7 : 1
              }}
              disabled={loading}
            >
              <div style={{
                backgroundColor: '#dc2626',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid rgba(255,255,255,0.3)'
              }}>
                <FontAwesomeIcon icon={faKey} style={{ fontSize: '14px' }} />
              </div>
              <div style={{
                padding: '10px 12px',
                flex: 1,
                textAlign: 'center'
              }}>
                {loading ? 'UPDATING...' : 'CHANGE PASSWORD'}
              </div>
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Profile;
